/**
 * StorageTab Component
 * Storage statistics and management panel for MonitoringPage
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/common';
import { useI18n } from '@/i18n';
import {
  useSystemResourceStats,
  useCleanupTempFiles,
  useCleanupExpiredArtifacts,
} from '../hooks/useResourceStats';
import { formatFileSize } from '@/types/resources';
import {
  HardDrive,
  Archive,
  Image,
  FileStack,
  Users,
  Trash2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Clock,
} from 'lucide-react';

interface CleanupDialogState {
  type: 'temp' | 'artifacts' | null;
  daysOld?: number;
}

export const StorageTab: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.resources;
  const [cleanupDialog, setCleanupDialog] = useState<CleanupDialogState>({ type: null });
  const [cleanupResult, setCleanupResult] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useSystemResourceStats(true, 10);

  const cleanupTempMutation = useCleanupTempFiles();
  const cleanupArtifactsMutation = useCleanupExpiredArtifacts();

  const handleCleanup = async () => {
    try {
      if (cleanupDialog.type === 'temp') {
        const result = await cleanupTempMutation.mutateAsync({
          days_old: cleanupDialog.daysOld || 7,
          dry_run: false,
        });
        setCleanupResult({
          type: 'success',
          message: t.cleanup.successTemp
            .replace('{count}', String(result.deleted_count))
            .replace('{size}', formatFileSize(result.deleted_size_bytes)),
        });
      } else if (cleanupDialog.type === 'artifacts') {
        const result = await cleanupArtifactsMutation.mutateAsync({
          delete_files: true,
        });
        setCleanupResult({
          type: 'success',
          message: t.cleanup.successArtifacts
            .replace('{count}', String(result.deleted_count))
            .replace('{size}', formatFileSize(result.deleted_size_bytes)),
        });
      }
      setCleanupDialog({ type: null });
      refetch();
    } catch (err) {
      setCleanupResult({
        type: 'error',
        message: err instanceof Error ? err.message : t.cleanup.error,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading message={messages.common.loading} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
        <div>
          <h3 className="text-lg font-semibold text-red-900 mb-1">
            {t.errors.loadFailed}
          </h3>
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : messages.common.error}
          </p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Timestamp and Refresh */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {t.lastUpdated}: {new Date(stats.timestamp).toLocaleString()}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {messages.common.refresh}
        </Button>
      </div>

      {/* Cleanup Result Message */}
      {cleanupResult && (
        <div
          className={`p-4 rounded-lg flex items-start gap-3 ${
            cleanupResult.type === 'success'
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          {cleanupResult.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <p className={`text-sm ${cleanupResult.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
            {cleanupResult.message}
          </p>
          <button
            onClick={() => setCleanupResult(null)}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t.stats.totalFiles}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_files.toLocaleString()}</p>
            </div>
            <HardDrive className="w-10 h-10 text-blue-100" />
          </div>
        </Card>
        <Card className="bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t.stats.totalSize}</p>
              <p className="text-2xl font-bold text-blue-600">{formatFileSize(stats.total_size_bytes)}</p>
            </div>
            <Archive className="w-10 h-10 text-blue-100" />
          </div>
        </Card>
        <Card className="bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t.stats.uniqueUsers}</p>
              <p className="text-2xl font-bold text-purple-600">{stats.unique_users}</p>
            </div>
            <Users className="w-10 h-10 text-purple-100" />
          </div>
        </Card>
        <Card className="bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{t.stats.avgPerUser}</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.unique_users > 0
                  ? formatFileSize(stats.total_size_bytes / stats.unique_users)
                  : '0 B'}
              </p>
            </div>
            <HardDrive className="w-10 h-10 text-green-100" />
          </div>
        </Card>
      </div>

      {/* By Type Breakdown */}
      <Card className="bg-white">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Archive className="w-5 h-5 text-gray-400" />
          {t.stats.byType}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Artifacts */}
          <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">{t.types.artifact}</span>
              </div>
              <span className="text-sm text-blue-600">{stats.artifacts.total_files} {t.stats.files}</span>
            </div>
            <p className="text-2xl font-bold text-blue-700 mb-2">
              {formatFileSize(stats.artifacts.total_size_bytes)}
            </p>
            <p className="text-xs text-blue-600">
              {stats.artifacts.unique_users} {t.stats.users}
            </p>
            {Object.keys(stats.artifacts.by_subtype).length > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-600 mb-1">{t.stats.subtypes}:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(stats.artifacts.by_subtype).map(([subtype, count]) => (
                    <span
                      key={subtype}
                      className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700"
                    >
                      {subtype}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Library */}
          <div className="p-4 rounded-lg border border-green-200 bg-green-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Image className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">{t.types.library}</span>
              </div>
              <span className="text-sm text-green-600">{stats.library.total_files} {t.stats.files}</span>
            </div>
            <p className="text-2xl font-bold text-green-700 mb-2">
              {formatFileSize(stats.library.total_size_bytes)}
            </p>
            <p className="text-xs text-green-600">
              {stats.library.unique_users} {t.stats.users}
            </p>
            {Object.keys(stats.library.by_subtype).length > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs text-green-600 mb-1">{t.stats.categories}:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(stats.library.by_subtype).map(([category, count]) => (
                    <span
                      key={category}
                      className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700"
                    >
                      {category}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Temp */}
          <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileStack className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-900">{t.types.temp}</span>
              </div>
              <span className="text-sm text-yellow-600">{stats.temp.total_files} {t.stats.files}</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700 mb-2">
              {formatFileSize(stats.temp.total_size_bytes)}
            </p>
            <p className="text-xs text-yellow-600">
              {stats.temp.unique_users} {t.stats.users}
            </p>
            <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t.stats.autoCleanup}
            </p>
          </div>
        </div>
      </Card>

      {/* Top Users */}
      {stats.largest_users && stats.largest_users.length > 0 && (
        <Card className="bg-white">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-400" />
            {t.stats.largestUsers}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t.table.userId}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.table.totalFiles}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.table.totalSize}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.types.artifact}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.types.library}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.types.temp}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.largest_users.map((user, index) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="text-sm font-mono text-gray-900">{user.user_id.slice(0, 16)}...</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      {user.total_files}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-blue-600">
                      {formatFileSize(user.total_size_bytes)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-500">
                      {user.artifacts_count}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-500">
                      {user.library_count}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-500">
                      {user.temp_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Cleanup Actions */}
      <Card className="bg-white">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-gray-400" />
          {t.cleanup.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Cleanup Temp Files */}
          <div className="p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">{t.cleanup.tempTitle}</h4>
            <p className="text-sm text-gray-500 mb-4">{t.cleanup.tempDesc}</p>
            {cleanupDialog.type === 'temp' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t.cleanup.daysOld}</label>
                  <select
                    value={cleanupDialog.daysOld || 7}
                    onChange={(e) => setCleanupDialog({ ...cleanupDialog, daysOld: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value={1}>1 {t.cleanup.days}</option>
                    <option value={3}>3 {t.cleanup.days}</option>
                    <option value={7}>7 {t.cleanup.days}</option>
                    <option value={14}>14 {t.cleanup.days}</option>
                    <option value={30}>30 {t.cleanup.days}</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleCleanup}
                    disabled={cleanupTempMutation.isPending}
                  >
                    {cleanupTempMutation.isPending ? t.cleanup.cleaning : t.cleanup.confirm}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCleanupDialog({ type: null })}
                  >
                    {t.actions.cancel}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setCleanupDialog({ type: 'temp', daysOld: 7 })}
              >
                <FileStack className="w-4 h-4 mr-2" />
                {t.cleanup.tempButton}
              </Button>
            )}
          </div>

          {/* Cleanup Expired Artifacts */}
          <div className="p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">{t.cleanup.artifactsTitle}</h4>
            <p className="text-sm text-gray-500 mb-4">{t.cleanup.artifactsDesc}</p>
            {cleanupDialog.type === 'artifacts' ? (
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleCleanup}
                  disabled={cleanupArtifactsMutation.isPending}
                >
                  {cleanupArtifactsMutation.isPending ? t.cleanup.cleaning : t.cleanup.confirm}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCleanupDialog({ type: null })}
                >
                  {t.actions.cancel}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setCleanupDialog({ type: 'artifacts' })}
              >
                <Archive className="w-4 h-4 mr-2" />
                {t.cleanup.artifactsButton}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
